import Vue from 'vue';
import types from '../mutation-types';
import authAPI from '../../api/auth';

import { setUser, clearCookiesOnLogout } from '../utils/api';
import { initializeAudioAlerts } from '../../helper/scriptHelpers';

let uiSettingsUpdateSequence = 0;

const initialState = {
  currentUser: {
    id: null,
    account_id: null,
    accounts: [],
    email: null,
    name: null,
  },
  uiFlags: {
    isFetching: true,
  },
};

// getters
export const getters = {
  isLoggedIn($state) {
    return !!$state.currentUser.id;
  },

  getCurrentUserID($state) {
    return $state.currentUser.id;
  },

  getUISettings($state) {
    return $state.currentUser.ui_settings || {};
  },

  getAuthUIFlags($state) {
    return $state.uiFlags;
  },

  getCurrentUserAvailability($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.availability;
  },

  getCurrentUserAutoOffline($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.auto_offline;
  },

  getCurrentAccountId(_, __, rootState) {
    if (rootState.route.params && rootState.route.params.accountId) {
      return Number(rootState.route.params.accountId);
    }
    return null;
  },

  getCurrentRole($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.role;
  },

  getCurrentUser($state) {
    return $state.currentUser;
  },

  getMessageSignature($state) {
    const { message_signature: messageSignature } = $state.currentUser;

    return messageSignature || '';
  },

  getSignatureLogoUrl($state) {
    return $state.currentUser.signature_logo_url || '';
  },

  getEmailMessageSignature($state) {
    const {
      message_signature: messageSignature,
      signature_logo_url: signatureLogoUrl,
    } = $state.currentUser;

    const signature = messageSignature || '';
    if (signatureLogoUrl) {
      const logoMarkdown = `![signature logo](${signatureLogoUrl})`;
      return signature ? `${logoMarkdown}\n\n${signature}` : logoMarkdown;
    }
    return signature;
  },

  getCurrentAccount($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount || {};
  },

  getUserAccounts($state) {
    const { accounts = [] } = $state.currentUser;
    return accounts;
  },
  
  getbaseUrl() {
    const base_url=window.location.href.split('/app/accounts')[0];
    return base_url;
  }
};

// actions
export const actions = {
  async validityCheck(context) {
    try {
      const response = await authAPI.validityCheck();
      const currentUser = response.data.payload.data;
      setUser(currentUser);
      context.commit(types.SET_CURRENT_USER, currentUser);
    } catch (error) {
      if (error?.response?.status === 401) {
        clearCookiesOnLogout();
      }
    }
  },
  async setUser({ commit, dispatch }) {
    if (authAPI.hasAuthCookie()) {
      await dispatch('validityCheck');
    } else {
      commit(types.CLEAR_USER);
    }
    commit(types.SET_CURRENT_USER_UI_FLAGS, { isFetching: false });
  },
  logout({ commit }) {
    commit(types.CLEAR_USER);
  },

  updateProfile: async ({ commit }, params) => {
    // eslint-disable-next-line no-useless-catch
    try {
      const response = await authAPI.profileUpdate(params);
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      throw error;
    }
  },

  deleteAvatar: async ({ commit, state }) => {
    // eslint-disable-next-line no-useless-catch
    try {
      await authAPI.deleteAvatar();
      commit(types.SET_CURRENT_USER, {
        ...state.currentUser,
        avatar_url: '',
      });
    } catch (error) {
      throw error;
    }
  },

  deleteSignatureLogo: async ({ commit, state }) => {
    // eslint-disable-next-line no-useless-catch
    try {
      await authAPI.deleteSignatureLogo();
      commit(types.SET_CURRENT_USER, {
        ...state.currentUser,
        signature_logo_url: '',
      });
    } catch (error) {
      throw error;
    }
  },

  patchAvatarUrl: ({ commit, state }, avatarUrl) => {
    commit(types.SET_CURRENT_USER, {
      ...state.currentUser,
      avatar_url: avatarUrl || '',
    });
  },

  updateUISettings: async ({ commit, state: $state }, params) => {
    const sequence = ++uiSettingsUpdateSequence;
    const previousUser = JSON.parse(JSON.stringify($state.currentUser));
    try {
      commit(types.SET_CURRENT_USER_UI_SETTINGS, params);
      const response = await authAPI.updateUISettings(params);

      if (sequence !== uiSettingsUpdateSequence) {
        return 'superseded';
      }

      commit(types.SET_CURRENT_USER, response.data);
      // Keep in-memory audio helper in sync without requiring a full page reload
      initializeAudioAlerts(response.data || $state.currentUser);
      return true;
    } catch (error) {
      if (sequence === uiSettingsUpdateSequence) {
        commit(types.SET_CURRENT_USER, previousUser);
        initializeAudioAlerts(previousUser);
      }
      return false;
    }
  },

  updateAvailability: async ({ commit, dispatch }, params) => {
    try {
      const response = await authAPI.updateAvailability(params);
      const userData = response.data;
      const { id } = userData;
      commit(types.SET_CURRENT_USER, response.data);
      dispatch('agents/updateSingleAgentPresence', {
        id,
        availabilityStatus: params.availability,
      });
    } catch (error) {
      // Ignore error
    }
  },

  updateAutoOffline: async ({ commit }, { accountId, autoOffline }) => {
    try {
      const response = await authAPI.updateAutoOffline(accountId, autoOffline);
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      // Ignore error
    }
  },

  setCurrentUserAvailability({ commit, state: $state }, data) {
    if (data[$state.currentUser.id]) {
      commit(types.SET_CURRENT_USER_AVAILABILITY, data[$state.currentUser.id]);
    }
  },

  setActiveAccount: async (_, { accountId }) => {
    try {
      await authAPI.setActiveAccount({ accountId });
    } catch (error) {
      // Ignore error
    }
  },
};

// mutations
export const mutations = {
  [types.SET_CURRENT_USER_AVAILABILITY](_state, availability) {
    const accounts = _state.currentUser.accounts.map(account => {
      if (account.id === _state.currentUser.account_id) {
        return { ...account, availability, availability_status: availability };
      }
      return account;
    });
    Vue.set(_state, 'currentUser', {
      ..._state.currentUser,
      accounts,
    });
  },
  [types.CLEAR_USER](_state) {
    _state.currentUser = initialState.currentUser;
  },
  [types.SET_CURRENT_USER](_state, currentUser) {
    Vue.set(_state, 'currentUser', currentUser);
  },
  [types.SET_CURRENT_USER_UI_SETTINGS](_state, { uiSettings }) {
    Vue.set(_state, 'currentUser', {
      ..._state.currentUser,
      ui_settings: {
        ..._state.currentUser.ui_settings,
        ...uiSettings,
      },
    });
  },

  [types.SET_CURRENT_USER_UI_FLAGS](_state, { isFetching }) {
    Vue.set(_state, 'uiFlags', { isFetching });
  },
};

export default {
  state: initialState,
  getters,
  actions,
  mutations,
};
